import React from 'react';
import { trendValenceClasses, type TrendValence } from '../lib/trend';
import { cn } from '../lib/utils';
import { TrendingUp, TrendingDown, Minus, type LucideIcon } from 'lucide-react';

export interface SummaryCardTrend {
  direction: 'up' | 'down' | 'flat';
  value: string;
  label?: string;
}

export interface SummaryCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  value?: string | number;
  subtitle?: string;
  description?: string;
  icon?: LucideIcon;
  trend?: SummaryCardTrend;
  trendValence?: TrendValence;
  /** Pass child elements for custom mini-visualizations */
  children?: React.ReactNode;
}

const trendConfig = { up: TrendingUp, down: TrendingDown, flat: Minus };

function SummaryCard({
  title,
  value,
  subtitle,
  description,
  icon: Icon,
  trend,
  trendValence = 'neutral',
  children,
  className,
  ...props
}: SummaryCardProps) {
  const subText = subtitle || description;

  return (
    <div
      className={cn(
        'relative min-w-0 overflow-hidden rounded-card border border-border bg-surface-sheet p-4 transition-interactive duration-200 ease-out',
        className,
      )}
      {...props}
    >
      <div className="mb-3 flex min-w-0 flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {Icon && (
            <Icon className="h-4 w-4 shrink-0 text-statura-green-accessible" aria-hidden="true" />
          )}
          <h4 className="min-w-0 break-words font-body text-body-sm text-text-muted">{title}</h4>
        </div>
        {trend &&
          trendConfig[trend.direction] &&
          (() => {
            const tc = trendConfig[trend.direction]!;
            return (
              <div
                className={cn(
                  'flex shrink-0 items-center gap-1 rounded px-2.5 py-1',
                  trendValenceClasses[trendValence],
                )}
              >
                {React.createElement(tc, {
                  'aria-hidden': true,
                  className: 'h-3.5 w-3.5 shrink-0',
                })}
                <span className="text-xs font-semibold">{trend.label || trend.value}</span>
              </div>
            );
          })()}
      </div>

      {value !== undefined && (
        <p className="min-w-0 break-words font-body text-metric text-text-heading tabular-nums">
          {value}
        </p>
      )}

      {subText && (
        <p className="mt-1 min-w-0 break-words font-body text-caption text-text-muted">{subText}</p>
      )}

      {children && <div className="mt-3 min-w-0">{children}</div>}
    </div>
  );
}

/** Grid wrapper for laying out multiple SummaryCards */
function SummaryCardGrid({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export { SummaryCard, SummaryCardGrid };
